'use client';

import { KeyboardEvent, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import styles from './SelectMenu.module.scss';
export interface SelectMenuOption {
  value: string;
  /** Plain text: type-ahead matching, and the display when no `content`. */
  label: string;
  /** Optional richer rendering of the label (e.g. aligned columns). */
  content?: ReactNode;
  /** Optional leading visual (icon, flag…). */
  icon?: ReactNode;
}
export interface SelectMenuProps {
  value: string;
  options: SelectMenuOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Marks the trigger invalid for styling and assistive tech. */
  invalid?: boolean;
  onBlur?: () => void;
  name?: string;
  /** Extra class on the trigger (e.g. to match a form's input height). */
  triggerClassName?: string;
  /** Extra class on the dropdown list. */
  menuClassName?: string;
  /** Icon at the start of the trigger, before the value. */
  leadingIcon?: ReactNode;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/** Long lists scroll inside the menu past this height. */
const MENU_MAX_HEIGHT = 264;

/**
 * Styled replacement for a native <select>: a pill trigger and a floating
 * listbox (rounded card, hover highlight, check on the selected option).
 * Keyboard: Enter/Space/↓/↑ open, ↑↓ Home End move, Enter selects, Esc
 * closes, typing a letter jumps to the next matching option. Always opens
 * below the trigger.
 */
const SelectMenu = ({
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  invalid = false,
  onBlur,
  name,
  triggerClassName,
  menuClassName,
  leadingIcon,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy
}: SelectMenuProps) => {
  const id = useId();
  const listId = `${id}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const typeahead = useRef({
    text: '',
    at: 0
  });
  const selectedIndex = options.findIndex(o => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const openMenu = (index = selectedIndex >= 0 ? selectedIndex : 0) => {
    if (disabled || options.length === 0) return;
    setActiveIndex(index);
    setOpen(true);
  };
  const close = (refocus = false) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };
  const choose = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    close(true);
  };

  // Close on a press anywhere outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  // Keep the active option in view while moving with the keyboard.
  useLayoutEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    el?.scrollIntoView({
      block: 'nearest'
    });
  }, [open, activeIndex]);
  const jumpByTyping = (key: string) => {
    const now = Date.now();
    const t = typeahead.current;
    t.text = now - t.at > 700 ? key : t.text + key;
    t.at = now;
    const query = t.text.toLowerCase();
    const start = open ? activeIndex + 1 : selectedIndex + 1;
    for (let i = 0; i < options.length; i++) {
      const index = (start + i) % options.length;
      if (options[index].label.toLowerCase().startsWith(query)) {
        if (open) setActiveIndex(index);else onChange(options[index].value);
        return;
      }
    }
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const last = options.length - 1;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openMenu();else setActiveIndex(i => Math.min(i + 1, last));
        return;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openMenu();else setActiveIndex(i => Math.max(i - 1, 0));
        return;
      case 'Home':
        if (open) {
          e.preventDefault();
          setActiveIndex(0);
        }
        return;
      case 'End':
        if (open) {
          e.preventDefault();
          setActiveIndex(last);
        }
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (open && activeIndex >= 0) choose(activeIndex);else openMenu();
        return;
      case 'Escape':
        if (open) {
          e.preventDefault();
          close();
        }
        return;
      case 'Tab':
        if (open) close();
        return;
      default:
        if (e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey) {
          jumpByTyping(e.key);
        }
    }
  };
  return <div className={styles.root} ref={rootRef} data-sentry-component="SelectMenu" data-sentry-source-file="SelectMenu.tsx">
            <button ref={triggerRef} type="button" name={name} className={`${styles.trigger} ${open ? styles.triggerOpen : ''} ${invalid ? styles.triggerInvalid : ''} ${triggerClassName ?? ''}`} disabled={disabled} role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined} aria-activedescendant={open && activeIndex >= 0 ? `${id}-opt-${activeIndex}` : undefined} aria-invalid={invalid || undefined} aria-label={ariaLabel} aria-labelledby={ariaLabelledBy} onClick={() => open ? close() : openMenu()} onKeyDown={onKeyDown} onBlur={onBlur}>
                <span className={`${styles.value} ${selected ? '' : styles.placeholder}`}>
                    {leadingIcon && <span className={styles.leading}>{leadingIcon}</span>}
                    {selected?.icon && <span className={styles.icon}>{selected.icon}</span>}
                    <span className={styles.label}>
                        {selected ? selected.content ?? selected.label : placeholder}
                    </span>
                </span>
                <ChevronDown size={18} className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden data-sentry-element="ChevronDown" data-sentry-source-file="SelectMenu.tsx" />
            </button>

            {open && <ul ref={listRef} id={listId} role="listbox" className={`${styles.menu} ${menuClassName ?? ''}`} style={{
      maxHeight: MENU_MAX_HEIGHT
    }}
    // Keep focus on the trigger so keyboard handling continues.
    onMouseDown={e => e.preventDefault()}>
                    {options.map((option, index) => {
        const isSelected = index === selectedIndex;
        return <li key={option.value} id={`${id}-opt-${index}`} role="option" aria-selected={isSelected} className={`${styles.option} ${index === activeIndex ? styles.optionActive : ''} ${isSelected ? styles.optionSelected : ''}`} onMouseEnter={() => setActiveIndex(index)} onClick={() => choose(index)}>
                                {option.icon && <span className={styles.icon}>{option.icon}</span>}
                                <span className={styles.label}>
                                    {option.content ?? option.label}
                                </span>
                                {isSelected && <Check size={16} className={styles.check} aria-hidden />}
                            </li>;
      })}
                </ul>}
        </div>;
};
export default SelectMenu;